import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ServerTokenService } from '@rocksoft/mdm-dep-sdk';
import { CertificateService, MdmVendorService, VppTokenService } from '@rocksoft/mdm-sdk-v2';
import { mergeMap, tap } from 'rxjs/operators';
import { safeMap } from '../effects.helpers';
import { HttpRequestService } from './http-request.service';
import {
  DepApiActions,
  DepCertificateApiActions,
  ManagementDepUiActions,
  ManagementPushUiActions,
  ManagementTokensDepApiActions,
  ManagementTokensDepCertificateActions,
  ManagementTokensDepResolverActions,
  ManagementTokensPushApiActions,
  ManagementTokensPushCsrActions,
  ManagementTokensPushResolverActions,
  ManagementTokensVendorApiActions,
  ManagementTokensVendorCsrActions,
  ManagementTokensVendorResolverActions,
  ManagementTokensVendorTokenActions,
  ManagementTokensVppApiActions,
  ManagementTokensVppResolverActions,
  ManagementVendorUiActions,
  ManagementVppUiActions,
  PushApiActions,
  VendorApiActions,
} from './management.actions';

@Injectable()
export class ManagementTokensEffects {
  private readonly actions = inject(Actions);
  private readonly mdmVppTokenService = inject(VppTokenService);
  private readonly mdmDepTokenService = inject(ServerTokenService);
  private readonly mdmVendorService = inject(MdmVendorService);
  private readonly certificateService = inject(CertificateService);
  private readonly httpRequestService = inject(HttpRequestService);

  // Vpp Token
  managementTokenVppRequestedEffect$ = createEffect(() => {
    return this.actions.pipe(
      ofType(ManagementTokensVppResolverActions.requested),
      mergeMap(() =>
        safeMap(
          this.mdmVppTokenService.tokensVppGet({ apiVersion: '2.0' }),
          (x) =>
            ManagementTokensVppApiActions.managementVppTokenLoaded({
              payload: { expirationDate: new Date(x.expirationDate ?? 0) },
            }),
          (error) => ManagementTokensVppApiActions.managementVppTokenLoadedError({ error })
        )
      )
    );
  });

  managementTokenVppUpdatedToken$ = createEffect(() => {
    return this.actions.pipe(
      ofType(ManagementVppUiActions.tokenUploaded),
      mergeMap(({ token }) =>
        safeMap(
          this.mdmVppTokenService.tokensVppPut({
            apiVersion: '2.0',
            businessAccountId: undefined,
            baseToken: { base64UserToken: new TextDecoder().decode(token) },
          }),
          () => {
            const expirationDate = new Date();
            expirationDate.setFullYear(expirationDate.getFullYear() + 1);
            return ManagementTokensVppApiActions.managementVppTokenUpdated({ payload: { expirationDate } });
          },
          (error) => ManagementTokensVppApiActions.managementVppTokenUpdatedError({ error })
        )
      )
    );
  });

  // Dep Token
  managementTokenDepRequestedEffect$ = createEffect(() => {
    return this.actions.pipe(
      ofType(ManagementTokensDepResolverActions.requested),
      mergeMap(() =>
        safeMap(
          this.mdmDepTokenService.tokensServerTokenGet(),
          (x) =>
            ManagementTokensDepApiActions.managementDepTokenLoaded({
              payload: { expirationDate: new Date(x.expirationDate ?? 0) },
            }),
          (error) => ManagementTokensDepApiActions.managementDepTokenLoadedError({ error })
        )
      )
    );
  });

  managementTokenDepUpdatedToken$ = createEffect(() => {
    return this.actions.pipe(
      ofType(ManagementDepUiActions.tokenUploaded),
      mergeMap(({ token }) =>
        safeMap(
          this.mdmDepTokenService.uploadServerToken({
            businessAccountId: undefined,
            serverTokenFile: new Blob([token], { type: 'application/pkcs7-mime' }),
          }),
          () => {
            const expirationDate = new Date();
            expirationDate.setFullYear(expirationDate.getFullYear() + 1);
            return DepApiActions.managementDepTokenUpdated({ payload: { expirationDate } });
          },
          (error) => DepApiActions.managementDepTokenUpdatedError({ error })
        )
      )
    );
  });

  // Push Certificate
  managementTokenPushRequestedEffect$ = createEffect(() => {
    return this.actions.pipe(
      ofType(ManagementTokensPushResolverActions.requested),
      mergeMap(() =>
        safeMap(
          this.certificateService.getPushCertificateRequest({ apiVersion: '2.0' }),
          (x) =>
            ManagementTokensPushApiActions.managementPushTokenLoaded({
              payload: { expirationDate: new Date(x.expirationDate ?? 0) },
            }),
          (error) => ManagementTokensPushApiActions.managementPushTokenLoadedError({ error })
        )
      )
    );
  });

  managementTokenPushCsrRequestedEffect$ = createEffect(() => {
    return this.actions.pipe(
      ofType(ManagementTokensPushCsrActions.downloadRequested),
      mergeMap(() =>
        safeMap(
          this.httpRequestService.startPushCertificateRequest('2.0', true).pipe(
            tap((res: Blob) => {
              this.createDownloadFile(res, 'application/x-plist', 'push', 'csr');
            })
          ),
          () => ManagementTokensPushApiActions.managementPushCSRDownloaded(),
          (error) => ManagementTokensPushApiActions.managementPushCSRDownloadedError({ error })
        )
      )
    );
  });

  managementTokenPushUpdatedToken$ = createEffect(() => {
    return this.actions.pipe(
      ofType(ManagementPushUiActions.tokenUploaded),
      mergeMap(({ token }) =>
        safeMap(
          this.certificateService.completePushCertificateRequest({
            apiVersion: '2.0',
            pushCertificate: new Blob([token], { type: 'application/pkcs7-mime' }),
          }),
          () => {
            const expirationDate = new Date();
            expirationDate.setFullYear(expirationDate.getFullYear() + 1);
            return PushApiActions.managementPushTokenUpdated({ payload: { expirationDate } });
          },
          (error) => PushApiActions.managementPushTokenUpdatedError({ error })
        )
      )
    );
  });

  // Dep Certificate
  managementTokenDepCertificateRequestedEffect$ = createEffect(() => {
    return this.actions.pipe(
      ofType(ManagementTokensDepCertificateActions.downloadRequested),
      mergeMap(() =>
        safeMap(
          this.httpRequestService.getPemRequest('2.0').pipe(
            tap((res: Blob) => {
              this.createDownloadFile(res, 'application/x-pem-file', 'dep', 'pem');
            })
          ),
          () => DepCertificateApiActions.managementDepCertificateDownloaded(),
          (error) => DepCertificateApiActions.managementDepCertificateDownloadedError({ error })
        )
      )
    );
  });

  // Vendor Certificate
  managementTokenVendorRequestedEffect$ = createEffect(() => {
    return this.actions.pipe(
      ofType(ManagementTokensVendorResolverActions.requested),
      mergeMap(() =>
        safeMap(
          this.mdmVendorService.getCertificate({ apiVersion: '2.0' }),
          (x) =>
            ManagementTokensVendorApiActions.managementVendorTokenLoaded({
              payload: { expirationDate: new Date(x.expirationDate ?? 0) },
            }),
          (error) => ManagementTokensVendorApiActions.managementVendorTokenLoadedError({ error })
        )
      )
    );
  });

  managementTokenVendorTokenDownloadRequestedEffect$ = createEffect(() => {
    return this.actions.pipe(
      ofType(ManagementTokensVendorTokenActions.downloadRequested),
      mergeMap(() =>
        safeMap(
          this.httpRequestService.downloadCer('2.0').pipe(
            tap((res: Blob) => {
              this.createDownloadFile(res, 'application/octet-stream', 'vendor', 'pfx');
            })
          ),
          () => ManagementTokensVendorApiActions.managementVendorTokenDownloaded(),
          (error) => ManagementTokensVendorApiActions.managementVendorTokenDownloadedError({ error })
        )
      )
    );
  });

  managementTokenVendorCsrDownloadRequestedEffect$ = createEffect(() => {
    return this.actions.pipe(
      ofType(ManagementTokensVendorCsrActions.downloadRequested),
      mergeMap(() =>
        safeMap(
          this.httpRequestService.startVendorCertificateRequest('2.0').pipe(
            tap((res: Blob) => {
              this.createDownloadFile(res, 'application/octet-stream', 'vendor', 'csr');
            })
          ),
          () => ManagementTokensVendorApiActions.managementVendorCSRDownloaded(),
          (error) => ManagementTokensVendorApiActions.managementVendorCSRDownloadedError({ error })
        )
      )
    );
  });

  managementTokenVendorUpdatedToken$ = createEffect(() => {
    return this.actions.pipe(
      ofType(ManagementVendorUiActions.tokenUploaded),
      mergeMap(({ token }) =>
        safeMap(
          this.httpRequestService.completeVendorCertificate('2.0', token),
          () => {
            const expirationDate = new Date();
            expirationDate.setFullYear(expirationDate.getFullYear() + 1);
            return VendorApiActions.managementVendorTokenUpdated({ payload: { expirationDate } });
          },
          (error) => VendorApiActions.managementVendorTokenUpdatedError({ error })
        )
      )
    );
  });

  private createDownloadFile(res: Blob, type: string, name: string, extension: string) {
    // Create a new blob to add the file extension
    const blob = new Blob([res], { type: type });
    // Trigger the download
    const uri = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.download = `${name}.${extension}`;
    anchor.href = uri;
    anchor.click();
  }
}
